import React, { Component } from 'react';
import { html } from 'lit';
// AG Grid imports
import { AgGridReact } from 'ag-grid-react';
import {
    AllCommunityModule, 
    ModuleRegistry, 
    ClientSideRowModelModule, 
    ValidationModule,
    NumberEditorModule, 
    DateFilterModule, 
    NumberFilterModule, 
    TextFilterModule,
    CustomFilterModule, 
    CsvExportModule, 
    RowSelectionModule, 
    DateEditorModule
} from "ag-grid-community";
import {
    TreeDataModule, 
    ColumnsToolPanelModule, 
    FiltersToolPanelModule, 
    PivotModule,
    SetFilterModule, 
    StatusBarModule, 
    ExcelExportModule,
    LicenseManager,
} from "ag-grid-enterprise";

// Shared components and utilities
import { 
    AlertMessagesService, 
    MediaOpsService, 
    AGGRID_LICENCE_KEY, 
    Utils, 
    OmniIconComponent, 
    OmniToolbarComponent 
} from '../../../../../shared';

// Local imports
import { campaignColumnDefs, defaultColDef } from './campaignsV2TableColumnDefs';
import MoreInfoRenderer from '../moreInfoRenderer';
import ActionMenuRenderer from '../actionMenuRenderer';
import CustomCampaignFloatingFilter from './customCampaignFloatingFilter';
import { CustomStatusBar } from '../../../../../shared';
import { FilterCampaign } from '../campaign-filter';
import CustomLoadingCellRenderer from '../customLoadingCellRenderer';

// Register the required modules with the Grid
ModuleRegistry.registerModules([
    TreeDataModule,
    AllCommunityModule,
    ValidationModule,
    NumberEditorModule,
    ColumnsToolPanelModule,
    FiltersToolPanelModule,
    PivotModule,
    SetFilterModule,
    ClientSideRowModelModule,
    DateFilterModule,
    NumberFilterModule,
    TextFilterModule,
    StatusBarModule,
    CustomFilterModule,
    CsvExportModule,
    ExcelExportModule,
    RowSelectionModule,
    DateEditorModule
]);


LicenseManager.setLicenseKey(AGGRID_LICENCE_KEY);
class CampaignTableAgGridV2 extends Component {
    // Campaign Cell Renderer - removed memoization to allow immediate expansion icon updates
    CampaignCellRenderer = (props) => {
        // Handle loading row
        if (props.data?.isLoadingRow) {
            return <CustomLoadingCellRenderer colDef={props.colDef} />;
        }
    
        const handleExpandClick = (e) => {
            e.stopPropagation();
            const isCurrentlyExpanded = props.node.expanded;
            props.node.setExpanded(!isCurrentlyExpanded);
    
            // Force immediate refresh of this cell to update the icon
            setTimeout(() => {
                if (props.api) {
                    props.api.refreshCells({
                        rowNodes: [props.node],
                        force: true
                    });
                }
            }, 0);
        };
    
        const handleTextClick = (e) => {
            props.context.componentParent.navigateToPath(props.data, 'campaignDetails');
        };
        
        const gridDragOver = (event) => {
            const dragSupported = event.dataTransfer.types.length;

            if (dragSupported) {
            event.dataTransfer.dropEffect = "move";
            event.preventDefault();
            }
        };

        const gridDrop = (event) => {
            event.preventDefault();
            console.log("Drop event on ", event);
            const jsonData = event.dataTransfer.getData("application/json");
            const data = JSON.parse(jsonData);

            // if data missing or data has no it, do nothing
            if (!data || data.items.length === 0 || props.data.parent_id) {
            return;
            }
            mergeCampaigns(data);
        }
        /**
         * Handles merging of selected campaigns into the parent campaign.
         * Prepares the payload and calls the merge API.
         * Shows success or error messages based on the API response.
         */
        const mergeCampaigns = (data) => {
            this.props.setSpinnerCount(1);
            const selectedUnmapCampaigns = data?.items || [];
            const selectedUnmapCampaignsIndex = data?.indexs || [];
            const payload = {
                "parent_id": props.data?.synthetic_id,
                "amd_campaignid": props.data?.id,
                "mediaocean_id": props.data?.mediaocean_id,
                "export_status": props.data?.export_status,
                "campaign_taxonomy": props.data?.campaignTaxonomy,
                "campaign_taxonomy_display": props.data?.campaign_taxonomy_display,
                "taxonomy_schema": props.data?.taxonomy_schema,
                "updated_at": MediaOpsService.getTodaysDate(),
                "updated_by": this.props.omniProfile?.email,
                "client_code": props.data?.client_code,
                "start_date": props.data?.start_date.split(" ")[0],
                "end_date": props.data?.end_date.split(" ")[0]
            }

            if (!props.data?.synthetic_id) {
                payload["created_at"] = MediaOpsService.getTodaysDate();
                payload["created_by"] = this.props.omniProfile?.email;
            }

            const children = [];
            selectedUnmapCampaigns.forEach((campaign, index) => {
                const childObj = {};
                childObj['campaign_id'] = campaign?.synthetic_id;
                childObj['mediaocean_id'] = campaign?.mediaocean_id;
                childObj['channels'] = campaign?.media_attributes?.channels?.map(channel => channel.channel_name).toString();
                childObj['start_date'] = campaign?.start_date.split("T")[0];
                childObj['end_date'] = campaign?.end_date.split("T")[0];
                children.push(childObj);
            });
            payload['children'] = children;
            let campaignId = encodeURIComponent(props.data?.id);
            MediaOpsService.mergeCampaigns(campaignId, payload).then((res) => {
                AlertMessagesService.showSuccessToastMessage(`${selectedUnmapCampaigns.length} campaign(s) successfully added to ${props.data?.campaignName}.`);
                let unMapCampaign = this.props.unMapCampaignObj.filter((item, index) => !selectedUnmapCampaignsIndex.includes(index));
                this.props.setUnMapCampaignObj(unMapCampaign);
                this.setState({ unMapCampaign: unMapCampaign });
                this.loadCampaigns(true);
                trackEvent('Campaigns are merged.', { access_from: 'Campaign Listing Page' });
            }).catch((error) => {
                console.log(error);
                AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => mergeCampaigns(data), title: 'Unable to add the campaigns', bodyMsgArr: [html`<p>${error?.response?.data}</p>`] }]);
                trackEvent('Campaigns are not merged.', { access_from: 'Campaign Listing Page', event: { error: error?.response?.data } });
            }).finally(() => {
                this.props.setSpinnerCount(0);
            });
        }

        const campaignName = props.data?.parent_id
            ? props.data?.campaignTaxonomy
            : props.data?.campaignDisplayName;
    
        // Check for children: either child_count > 0 OR nestedData exists
        const hasChildren = (props.data?.child_count > 0) ||
            (props.data?.nestedData && props.data.nestedData.length > 0);
        const level = props.node.level || 0;
    
        // For parent campaigns
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                minHeight: '42px',
                paddingLeft: `${level * 20}px`,
            }}>
                {hasChildren ? (
                    <OmniIconComponent
                        className="is-size-3"
                        icon-id={props.node.expanded ? 'omni:interactive:down' : 'omni:interactive:right'}
                        onClick={handleExpandClick}
                        style={{ marginRight: '10px', cursor: 'pointer', flexShrink: 0, marginTop: '2px' }}
                    />
                ) : (
                    <span style={{ width: '16px', marginRight: '8px', flexShrink: 0 }}></span>
                )}
                <span
                    onClick={handleTextClick}
                    onDragOver={gridDragOver}
                    onDrop={(e) => gridDrop(e)}   
                    style={{
                        cursor: 'pointer',
                        flex: 1,
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal'
                    }}
                >
                    {campaignName}
                </span>
            </div>
        );
    };

    filterOptions = {
        campaignName: '',
        startDate: '',
        endDate: '',
        budgetMin: '',
        budgetMax: '',
        clientName: ''
    };

        applyRenderers = (cols) => {
        const cellRenderers = {
            actions: this.actionCellRenderer,
            planned_netmedia: this.plannedNetMediaRenderer,
            planned_budget: this.plannedBudgetRenderer,
            planned_fees: this.plannedFeesRenderer,
            ordered_budget: this.orderedBudgetRenderer,
            ordered_netmedia: this.orderedNetMediaRenderer,
            ordered_fees: this.orderedFeesRenderer,
            mediaopshub_status: this.mediaopshubStatusRenderer,
            mediaocean_status: this.mediaOceanStatusRenderer,
            taxonomy_status: this.taxonomyStatusRenderer,
            dateRange: this.dateRangeRenderer,
        };

        return cols.map(col => {
            // if column has children → recurse
            if (col.children) {
                return {
                    ...col,
                    children: this.applyRenderers(col.children),
                };
            }

            // otherwise apply renderer if matched
            return cellRenderers[col.field]
                ? { ...col, cellRenderer: cellRenderers[col.field] }
                : col;
        });
    };

    defaultState = {
            columnDefs: this.applyRenderers(campaignColumnDefs),
            autoGroupColumnDef: {
                headerName: "CAMPAIGN",
                field: "campaignDisplayName",
                wrapText: true,
                autoHeight: true,
                minWidth: 500,
                flex: 6,
                suppressHeaderMenuButton: true,
                cellRenderer: this.CampaignCellRenderer,
                floatingFilter: false,
                floatingFilterComponent: CustomCampaignFloatingFilter,
                filter: false,
                suppressFloatingFilterButton: false,
                comparator: () => 0,
            },
            hasNext: false,
            archiveTab: false,
            totalCount: 0,
            errorObject: { displayError: false },
            campaignsNestedStructure: [],
            campaignsFromAPI: [],
            openMoveDropdownId: null,
            openActionMenuId: null,
            enableMerge: false,
            enableUnmerge: false,
            selectedCampaigns: [],
            displayMergeModal: false,
            sortingEnable: false,
            sortColumn: '',
            sortOrder: 'Desc',
            searchEnable: false,
            searchParam: '',
            searchColumn: '',
            // Unmapped campaigns fetched separately; used to show count in the warning bar
            unMapCampaign :this.props?.unMapCampaignObj || [],
        };

    constructor(props) {
        super(props);
        this.gridRef = React.createRef();
        this.state = this.defaultState;
    }

    componentDidMount() {
        const hasStoredState = this.props?.campaignTableData?.tableState && !this.props?.childCampaignIsCreated;
        this.props.setSpinnerCount(0);
        if (hasStoredState) {
            this.restoreTableState();
        } else {
            this.loadCampaigns(true);
            if (this.props?.setChildCampaignIsCreated) { // if the child campaign is created, then set the flag to false
                this.props?.setChildCampaignIsCreated(false);
            }
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // Compare current state with previous state
        if (prevProps.isMappingTable !== this.props.isMappingTable) {
            this.gridRef?.current?.api?.refreshCells({ force: true });
        }

        if (prevProps.versionOneApi !== this.props.versionOneApi) {
            this.loadCampaigns(true);
        }
    }

    restoreTableState = () => {
        const { tableState, savedScrollTop, savedScrollLeft } = this.props.campaignTableData;

        // Cache scroll positions
        this.savedScrollTop = savedScrollTop;
        this.savedScrollLeft = savedScrollLeft;

        // Restore state with safe fallbacks
        this.setState({
            ...this.defaultState,
            campaignsNestedStructure: tableState?.campaignsNestedStructure || [],
            hasNext: tableState?.hasNext || false,
            errorObject: tableState?.errorObject || { displayError: false },
            searchParam: tableState?.searchParam || '',
            totalCount: tableState?.totalCount || 0,
            archiveTab: tableState?.archiveTab || false,
            displayMergeModal: tableState?.displayMergeModal || false,
            selectedCampaigns: tableState?.selectedCampaigns || [],
            openActionMenuId: tableState?.openActionMenuId || null,
            openMoveDropdownId: tableState?.openMoveDropdownId || null,
            campaignsFromAPI: tableState?.campaignsFromAPI || [],
            enableMerge: tableState?.enableMerge || false,
            enableUnmerge: tableState?.enableUnmerge || false,
            sortingEnable: tableState?.sortingEnable || false,
            sortColumn: tableState?.sortColumn || '',
            sortOrder: tableState?.sortOrder || 'Desc',
            searchEnable: tableState?.searchEnable || false,
            searchColumn: tableState?.searchColumn || '',
            // Unmapped campaigns fetched separately; used to show count in the warning bar
            unMapCampaign : tableState?.unMapCampaign || this.props?.unMapCampaignObj || [],
        }, () => {
            // Allow grid to render before restoring UI state
            setTimeout(() => {
                this.gridRef?.current?.api?.closeToolPanel();
                this.gridRef?.current?.api?.refreshCells({ force: true });
                this.restoreScrollPosition();
            }, 100);
        });
    };

    plannedNetMediaRenderer = (props) => {
        const isLocal = this.props?.systemParametersData?.data?.isLocal;
        if (isLocal) {
            const displayValue = Utils.getLocalCurrency(props?.data?.local_currency_name ? props?.data?.local_currency_name : 'USD', props?.data?.planned_netmedia_local)
            return (<span>{displayValue}</span>)
        } else {
            const displayValue = Utils.getLocalCurrency('USD', props?.data?.planned_netmedia)
            return (<span>{displayValue}</span>)
        }
    }

    plannedBudgetRenderer = (props) => {
        const isLocal = this.props?.systemParametersData?.data?.isLocal;
        if (isLocal) {
            const displayValue = Utils.getLocalCurrency(props?.data?.local_currency_name ? props?.data?.local_currency_name : 'USD', props?.data?.planned_budget_local)
            return (<span>{displayValue}</span>)
        } else {            
            const displayValue = Utils.getLocalCurrency('USD', props?.data?.planned_budget)
            return (<span>{displayValue}</span>)
        }
    }

    plannedFeesRenderer = (props) => {
        const isLocal = this.props?.systemParametersData?.data?.isLocal;
        if (isLocal) {
            const displayValue = Utils.getLocalCurrency(props?.data?.local_currency_name ? props?.data?.local_currency_name : 'USD', props?.data?.planned_fees_local)
            return (<span>{displayValue}</span>)
        } else {
            const displayValue = Utils.getLocalCurrency('USD', props?.data?.planned_fees)
            return (<span>{displayValue}</span>)
        }
    }

    orderedNetMediaRenderer = (props) => {
        const isLocal = this.props?.systemParametersData?.data?.isLocal;
        if (isLocal) {
            const displayValue = Utils.getLocalCurrency(props?.data?.local_currency_name ? props?.data?.local_currency_name : 'USD', props?.data?.ordered_netmedia_local)
            return (<span>{displayValue}</span>)
        } else {
            const displayValue = Utils.getLocalCurrency('USD', props?.data?.ordered_netmedia)
            return (<span>{displayValue}</span>)
        }
    }

    orderedBudgetRenderer = (props) => {
        const isLocal = this.props?.systemParametersData?.data?.isLocal;
        if (isLocal) {
            const displayValue = Utils.getLocalCurrency(props?.data?.local_currency_name ? props?.data?.local_currency_name : 'USD', props?.data?.ordered_budget_local)
            return (<span>{displayValue}</span>)
        } else {
            const displayValue = Utils.getLocalCurrency('USD', props?.data?.ordered_budget)
            return (<span>{displayValue}</span>)
        }
    }

    orderedFeesRenderer = (props) => {
        const isLocal = this.props?.systemParametersData?.data?.isLocal;
        if (isLocal) {
            const displayValue = Utils.getLocalCurrency(props?.data?.local_currency_name ? props?.data?.local_currency_name : 'USD', props?.data?.ordered_fees_local)
            return (<span>{displayValue}</span>)
        } else {
            const displayValue = Utils.getLocalCurrency('USD', props?.data?.ordered_fees)
            return (<span>{displayValue}</span>)
        }
    }

    mediaopshubStatusRenderer = (props) => {
        const statusColor = MediaOpsService.getStatusColor(props?.data?.mediaopshub_status);
        return (
            <span style={{ color: statusColor }}>
                {props?.data?.mediaopshub_status}
            </span>
        );
    };

    mediaOceanStatusRenderer = (props) => {
        const statusColor = MediaOpsService.getStatusColor(props?.data?.mediaocean_status);
        return (
            <span style={{ color: statusColor }}>
                {props?.data?.mediaocean_status}
            </span>
        );
    };

    taxonomyStatusRenderer = (props) => {        
        const statusColor = MediaOpsService.getStatusColor(props?.data?.taxonomy_status);
        return (
            <span style={{ color: statusColor }}>
                {props?.data?.taxonomy_status}
            </span>
        );
    };

    actionCellRenderer = (props) => {
        if (this.state.archiveTab) {
            return <button className="secondary medium" onClick={this.unArchiveCampaign.bind(this, props.data)}>Restore</button>
        } else {
            const statusColor = MediaOpsService.getStatusColor(props?.data?.taxonomy_status);

            return (
                <div className="action-buttons">
                    <ActionMenuRenderer
                        {...props}
                        id={props?.node?.id + "actions"}
                        isOpen={this.state.openActionMenuId === props?.node?.id}
                        onToggle={this.toggleActionMenu}
                        campaign={props?.data}
                        navigate={this.navigateToPath}
                        setSpinnerCount={this.props?.setSpinnerCount}
                        versionOneApi={this.props?.versionOneApi}
                        fetchCampaignData={this.loadCampaigns}
                        isMappingTable={this.props?.isMappingTable}
                    ></ActionMenuRenderer>

                    <MoreInfoRenderer
                        {...props}
                        id={props?.node?.id + "moreInfo"}
                        isOpen={this.state.openMoveDropdownId === props?.node?.id}
                        onToggle={this.toggleMoreInfo}
                        campaign={props?.data}
                    ></MoreInfoRenderer>

                    <span style={{ color: statusColor }} className='taxonomy-status'>{props?.data?.taxonomy_status}</span>
                </div>
            )
        }
    };

    dateRangeRenderer = (props) => {
        const startDate = props?.data?.start_date;
        const endDate = props?.data?.end_date;
        const formattedStartDate = startDate ? Utils.formatDateObject(startDate) : null;
        const formattedEndDate = endDate ? Utils.formatDateObject(endDate) : null;

        return (
            <span>{formattedStartDate} - {formattedEndDate}</span>
        );
    }

    toggleMoreInfo = (id) => {
        const newId = this.state.openMoveDropdownId === id ? null : id;
        this.setState({ openMoveDropdownId: newId }); // Immediately update dropdown visibility
        setTimeout(() => { // Defer grid refresh slightly more than 0ms (AG Grid needs more time)
            this.gridRef?.current?.api?.refreshCells({ force: true });
        }, 50);
    };

    toggleActionMenu = (id) => {
        const newId = this.state.openActionMenuId === id ? null : id;
        this.setState({ openActionMenuId: newId }); // Immediately update dropdown visibility
        setTimeout(() => { // Defer grid refresh slightly more than 0ms (AG Grid needs more time)
            this.gridRef?.current?.api?.refreshCells({ force: true });
        }, 50);
    };

    /**
     * Saves the current vertical and horizontal scroll positions of the AG Grid viewports.
     * This method queries the DOM for the vertical ('.ag-body-viewport') and horizontal ('.ag-center-cols-viewport')
     * scroll containers of the AG Grid. If found, it stores their current scroll positions (`scrollTop` and `scrollLeft`)
     * in the instance variables `savedScrollTop` and `savedScrollLeft` respectively.
     * @returns {void}
    */
    saveScrollPosition = () => {
        const verticalViewport = document.querySelector('.ag-body-viewport');
        const horizontalViewport = document.querySelector('.ag-center-cols-viewport');
        if (verticalViewport) {
            this.savedScrollTop = verticalViewport.scrollTop;
        }
        if (horizontalViewport) {
            this.savedScrollLeft = horizontalViewport.scrollLeft;
        }
    };

    /**
     * Restores the scroll position of the AG Grid viewports (vertical and horizontal)
     * to previously saved values (`savedScrollTop` and `savedScrollLeft`).
     * Uses a slight delay to ensure the DOM elements are available before setting scroll positions.
     * @returns {void}
     */
    restoreScrollPosition = () => {
        setTimeout(() => {
            const verticalViewport = document.querySelector('.ag-body-viewport');
            const horizontalViewport = document.querySelector('.ag-center-cols-viewport');

            if (verticalViewport && this.savedScrollTop != null) {
                verticalViewport.scrollTop = this.savedScrollTop;
            }
            if (horizontalViewport && this.savedScrollLeft != null) {
                horizontalViewport.scrollLeft = this.savedScrollLeft;
            }
        }, 50); // Slight delay to ensure DOM is ready
    };

    /**
     * Handles searching within a specific column of the campaigns table.
     * Updates the component state to enable or disable search, sets the search parameters,
     * disables sorting, and triggers a reload of campaigns.
     * @param {string} columnName - The name of the column to search in.
     * @param {string} searchValue - The value to search for in the specified column.
     */
    columnSearch = (columnName, searchValue) => {
        this.props.setSpinnerCount(1);

        if (searchValue) {
            this.setState({
                searchEnable: true,
                searchColumn: columnName,
                searchParam: searchValue,
                sortingEnable: false,
                hasNext: ''
            }, () => { this.loadCampaigns(true) })
        } else {
            this.setState({
                searchEnable: false,
                searchColumn: '',
                searchParam: '',
                sortingEnable: false,
                hasNext: ''
            }, () => { this.loadCampaigns(true) })
        }
    }

    /**
    * The below code is defining a function called `resetStateAndUpdateCampaigns` in JavaScript. This
    * function is using the `setState` method to reset the state of the current component to its default
    * state, and then it is calling the `createData` method. 
    */
    resetStateAndUpdateCampaigns = () => {
        this.setState(this.defaultState, () => {
            this.props.setMappingTable(false);
            this.loadCampaigns()
        })
    }

    /**
     * Loads campaign data, optionally fetching fresh data or paginated results.
     * Handles spinner state, scroll position, data transformation, and error handling.
     * @param {boolean} [isFreshData=true] - If true, fetches fresh data; otherwise, loads next page if available.
     * @param {string|null} [parentId=null] - If provided, fetches campaigns for the specified parent campaign ID.
     * @returns {Promise<void>} Resolves when campaigns are loaded and state is updated.
     */
    loadCampaigns = async (isFreshData = true, parentId = null) => {
        if (!isFreshData && !this.state.hasNext && !parentId) {
            return;
        }

        this.props.setSpinnerCount(0);

        try {
            this.saveScrollPosition();
            this.props.setSpinnerCount(1);

            const response = await this.fetchCampaignData(isFreshData, parentId);
            if (!response) return;

            const campaignsFromAPI = this.processCampaignData(response, isFreshData);
            const uniqueCampaigns = this.removeDuplicateCampaigns(campaignsFromAPI, isFreshData);
            const campaignsNestedStructure = MediaOpsService.transformDataAggrid(uniqueCampaigns);
            if (isFreshData || this.state.unMapCampaign.length === 0) {
                this.getUnMapCampaign();
            }
            this.updateStateWithCampaigns(response, campaignsNestedStructure, campaignsFromAPI, parentId);

        } catch (error) {
            this.handleCampaignLoadError(error, isFreshData);
        }
    };

    /**
     * Fetches campaign data based on the current state of search, sorting, or default filters.
     * - If search is enabled, performs a search query.
     * - If sorting is enabled, fetches sorted data.
     * - Otherwise, fetches campaign data with applied filters.
     * Handles API errors and sets error state if a server error occurs.
     * @param {boolean} isFreshData - Indicates whether to fetch fresh data or use pagination.
     * @param {string|null} parentId - Optional parent campaign ID for fetching child campaigns.
     * @returns {Promise<Object|null>} The response object from the API, or null if an error occurs.
     */
    fetchCampaignData = async (isFreshData, parentId = null) => {
        try {
            let response;
            if (this.state.searchEnable) {
                response = await MediaOpsService.searchCampaignsAggrid(
                    this.state.searchParam,
                    this.state.archiveTab,
                    this.props?.versionOneApi,
                    this.state.hasNext
                );
            } else if (this.state.sortingEnable) {
                response = await MediaOpsService.sortCampaignsAggrid(
                    this.state.sortColumn,
                    this.state.sortOrder,
                    this.state.archiveTab,
                    this.props?.versionOneApi,
                    this.state.hasNext
                );
            } else {
                let filterOptions = !parentId ? { ...this.state.filterOptions, source: 'planned', 'page-count': 10 } : { 'source': 'children', 'parent-id': parentId.amd_campaign_id }
                if (parentId?.camapign_id) {
                    filterOptions['campaign-id'] = parentId.camapign_id;
                }
                let hasNext = !isFreshData && !parentId ? this.state.hasNext : '';
                response = await MediaOpsService.getCampaignDataAggrid(filterOptions, hasNext, this.state.archiveTab, this.props?.versionOneApi);
            }

            if (response?.data?.statusCode === 500) {
                this.setState({ errorObject: { displayError: true } });
                return null;
            }
            return response;
        } catch (error) {
            this.props.setSpinnerCount(0);
            AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.loadCampaigns(isFreshData, parentId), title: 'Error loading campaigns', bodyMsgArr: [] }]);
            return null;
        }
    };

    /**
     * Processes campaign data received from the API, preparing it for use in the component state.
     * @param {Object} response - The API response object containing campaign data.
     * @param {boolean} isFreshData - Indicates whether the data is a fresh set (true) or a paginated addition (false).
     * @returns {Array<Object>} An array of prepared campaign data objects. If `isFreshData` is true, returns the processed items directly.
     * If false, merges new items with existing ones, avoiding duplicates by `id`.
     */
    processCampaignData = (response, isFreshData) => {
        if (isFreshData) {
            return MediaOpsService.prepareCampaignData(response?.data?.Items);
        } else {
            const nextPageItems = MediaOpsService.prepareCampaignData(response?.data?.Items);
            return [
                ...this.state.campaignsFromAPI,
                ...nextPageItems.filter(
                    (nextItem) => !this.state.campaignsFromAPI.some((existingItem) => existingItem.id === nextItem.id)
                ),
            ];
        }
    };

    /**
     * Removes duplicate campaigns from the provided array based on the campaign's `id`.
     * @param {Array<Object>} campaigns - The array of campaign objects to filter.
     * @param {boolean} isFreshData - Indicates if the data is freshly fetched (currently unused).
     * @returns {Array<Object>} A new array containing only unique campaigns by `id`.
     */
    removeDuplicateCampaigns = (campaigns, isFreshData) => {
        return campaigns.reduce((acc, current) => {
            const exists = acc.find(item => item?.id?.toString() === current?.id?.toString());
            return exists ? acc : [...acc, current];
        }, []);
    };

    /**
     * Updates the component state with campaign data and triggers a grid refresh.
     * @param {Object} response - The API response containing campaign data and pagination info.
     * @param {Object} campaignsNestedStructure - The nested structure of campaigns to be set in state.
     * @param {Array} campaignsFromAPI - The flat array of campaigns fetched from the API.
     * @param {string|null} parentId - The ID of the parent campaign if applicable, used for child campaigns.
     */
    updateStateWithCampaigns = (response, campaignsNestedStructure, campaignsFromAPI, parentId = null) => {
        const hasNext = !parentId ? (response?.data?.paging?.next || '') : this.state.hasNext;
        let totalCount = !parentId ? (response?.data?.total_records || 0) : this.state.totalCount;
        this.setState(
            {
                campaignsNestedStructure,
                totalCount,
                hasNext,
                campaignsFromAPI,
            },
            () => { this.refreshGrid() }
        );
    };

    /**
     * Creates data for the campaign and retrieves it from the MediaOpsService.
     * If isFreshData is true, it sets the 'loaded' state to false before fetching data.
     * If campaignSentToMediaOcean is true, it checks the export status of the campaign and shows corresponding toast messages.
     * @param {boolean} isFreshData - Indicates whether to fetch fresh data (default is true).
     * @returns {void}
    */
    getUnMapCampaign = () => {
        let filterOptions = { 'source': 'unmapped' };

        MediaOpsService.getCampaignDataAggrid(filterOptions, null, this.state.archiveTab, this.props?.versionOneApi).then(response => {
            let unMapCampaign = [];
            let responseData = response['data']['Items'] ? response['data']['Items'] : [];
            let data = MediaOpsService.prepareCampaignData(responseData);

            unMapCampaign = [...data];
            // campaigns.push(...data);
            this.setState({ unMapCampaign });
            this.props.setUnMapCampaignObj(unMapCampaign);

        }).catch((error) => {
            this.setState({ loaded: true });
            this.props.setSpinnerCount(0);
            AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Refresh', retryBtnEvent: () => this.getUnMapCampaign(), title: 'Unmapped Campaign API cannot be loaded', bodyMsgArr: [] }]);
        });
    }

    /**
     * Refreshes the AG Grid by closing the tool panel, forcing cell refresh, and restoring the scroll position.
     * Also checks if more campaigns need to be loaded based on the grid's content and container heights.
     * Updates the spinner count accordingly.
     * @returns {void}
     */
    refreshGrid = () => {
        setTimeout(() => {
            this.gridRef?.current?.api?.closeToolPanel();
            this.gridRef?.current?.api?.refreshCells({ force: true });
            this.restoreScrollPosition();

            const containerDiv = document.querySelector('.ag-center-cols-viewport');
            const contentDiv = document.querySelector('.ag-center-cols-container');

            if (!this.state.hasNext) {
                this.props.setSpinnerCount(0);
            } else if (containerDiv?.clientHeight !== contentDiv?.clientHeight) {
                this.loadCampaigns(false);
            } else {
                this.props.setSpinnerCount(0);
            }
        }, 100);
    };

    /**
     * Handles errors that occur while loading campaigns.
     * Logs the error, resets the spinner count, and displays an alert message
     * with an option to retry loading the campaigns.
     * @param {Error} error - The error object encountered during campaign loading.
     * @param {boolean} isFreshData - Indicates whether the data being loaded is fresh.
     */
    handleCampaignLoadError = (error, isFreshData) => {
        this.props.setSpinnerCount(0);
        AlertMessagesService.showMultipleAlerts([
            {
                type: "danger",
                retryBtnText: "Refresh",
                retryBtnEvent: () => this.loadCampaigns(isFreshData),
                title: "Campaign API cannot be loaded",
                bodyMsgArr: [],
            },
        ]);
    };

    /**
     * Handles the cell click event in the campaigns table.
     * Navigates to the campaign details page if the clicked cell is in the "campaignName" column.
     * @param {Object} event - The event object from the cell click.
     * @param {Object} event.colDef - The column definition of the clicked cell.
     * @param {string} event.colDef.field - The field name of the clicked column.
     * @param {Object} event.data - The row data of the clicked cell.
     */
    onCellClicked = (event) => {
        if (event?.colDef?.field === "campaignName") {
            this.navigateToPath(event?.data, "campaignDetails")
        }
    }

    /**
     * Navigates to a specified path related to a campaign, saving the current table state and scroll position.
     * @param {Object} campaign - The campaign object to navigate from.
     * @param {string} path - The path to navigate to.
     * Side Effects:
     * - Sets a spinner count to indicate loading.
     * - Saves the current scroll position of the table.
     * - Stores the current table state and scroll positions in the Redux store.
     * - Determines the parent campaign if applicable.
     * - Navigates to the specified component using utility navigation.
     */
    navigateToPath = (campaign, path) => {
        this.props.setSpinnerCount(1);
        this.saveScrollPosition();
        this.props.setCampaignTableData({ tableState: this.state, savedScrollTop: this.savedScrollTop, savedScrollLeft: this.savedScrollLeft });
        let parentCampaign, selectedChildCampaigns = [];
        if (campaign?.parent_id) {
            parentCampaign = this.state.campaignsFromAPI.filter((item) => (item.id === campaign.parent_id || item.synthetic_id === campaign.parent_id))[0];
            selectedChildCampaigns = this.state.campaignsFromAPI.filter((item) => item.parent_id === parentCampaign.id || item.parent_id === parentCampaign.synthetic_id);
        } else if (campaign?.child_count > 0) {
            selectedChildCampaigns = this.state.campaignsFromAPI.filter((item) => (item.parent_id === campaign.id || item.parent_id === campaign.synthetic_id) );
        }
        Utils.navigateToComponent(campaign, path, this.props, {}, parentCampaign, selectedChildCampaigns)
    }

    /**
     * Callback fired when the AG Grid is ready.
     * Initializes grid API references and attaches a scroll event listener to the grid's body viewport.
     * @param {Object} params - The parameters provided by AG Grid on grid ready.
     * @param {Object} params.api - The grid API instance.
     * @param {Object} params.columnApi - The grid column API instance.
     */
    onGridReady = (params) => {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;

        // Listen for scroll events on the body viewport
        const gridBody = document.querySelector('.ag-body-viewport');

        if (gridBody) {
            gridBody.addEventListener('scroll', this.handleBodyScroll);
        }

        this.setState({hasNext: this.state.hasNext});
    };

    /**
     * Handles the scroll event on the table body to implement infinite scrolling.
     * When the user scrolls to the bottom, it checks if all campaigns have been loaded.
     * If not, it triggers loading more campaigns from the API.
     * @param {React.UIEvent<HTMLDivElement>} event - The scroll event triggered on the table body.
     */
    handleBodyScroll = (event) => {
        const target = event.target;
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;
        const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 1;

        if (scrolledToBottom) {
            if (this.state.totalCount === this.state.campaignsFromAPI.length) {
                return;
            }
            this.loadCampaigns(false); // You can trigger loading more data or any other action here
        }
    };

    /**
     * Handles the merge operation for selected campaigns.
     * - Identifies the parent campaign (with source "planit") and child campaigns from the selected campaigns.
     * - Validates that the dates of the child campaigns match the parent campaign using `Utils.compareDates`.
     * - Displays an error toast if the dates do not match.
     * - Ensures the parent campaign has both `synthetic_id` and `client_code` assigned.
     * - Displays an error toast if required fields are missing.
     * - If all validations pass, sets the state to display the merge modal.
     * @returns {void}
     */
    mergeCampaigns = () => {
        const parentCampaign = this.state.selectedCampaigns.find((campaign) => campaign.source === "planit");
        const childCampaigns = this.state.selectedCampaigns.filter((campaign) => campaign !== parentCampaign);
        let isDateMatched = Utils.compareDates(parentCampaign, childCampaigns);

        if (!isDateMatched) {
            AlertMessagesService.showErrorToastMessage('Child Campaigns dates are not matching with the parent campaign.');
            return
        }

        if (!parentCampaign.synthetic_id || !parentCampaign.client_code) {
            AlertMessagesService.showErrorToastMessage('Please update the campaign by assigning the client code');
            return
        }
        this.setState({ displayMergeModal: true })
    }

    /**
     * Unmerges selected campaigns by calling the MediaOpsService API.
     * - Sets a loading spinner while the operation is in progress.
     * - Identifies the parent campaign from the selected campaigns (with source "planit").
     * - Constructs a payload with parent campaign details and sends an unmerge request.
     * - On success, shows a success toast, resets campaign-related state, and reloads campaigns.
     * - On failure, displays an alert with a retry option.
     * - Always resets the loading spinner count after the operation.
     * @returns {void}
     */
    unmergeCampaigns = () => {
        this.props.setSpinnerCount(1);
        const parentCampaign = this.state.selectedCampaigns.filter((campaign) => campaign.source === "planit")[0];
        let campaignId = encodeURIComponent(parentCampaign?.id);
        const payload = {
            parent_synthetic_id: parentCampaign?.synthetic_id,
            parent_id: parentCampaign?.id,
            parent_status: parentCampaign?.export_status,
        };

        MediaOpsService.unmergeCampaigns(campaignId, payload, this.props?.versionOneApi).then((response) => {
            AlertMessagesService.showSuccessToastMessage('Campaigns are unmerged!');
            this.setState({
                campaignsFromAPI: [],
                hasNext: '',
                campaignsNestedStructure: [],
                totalCount: '',
                searchEnable: false,
                searchColumn: '',
                searchParam: '',
                sortingEnable: false,
                sortColumn: '',
                sortOrder: 'Desc',
            }, () => {
                this.loadCampaigns(true);
            })
        }, (error) => {
            AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.unmergeCampaigns, title: 'Unable to unmerged the campaigns', bodyMsgArr: [] }]);
        }).finally(() => { this.props.setSpinnerCount(0) });
    }

    /**
     * Closes the merge modal by setting `displayMergeModal` state to false.
     * Typically called when the user cancels a merge operation.
     */
    cancelMerge = () => {
        this.setState({ displayMergeModal: false })
    }

    /**
     * Checks if exactly one campaign with the source 'planit' is selected and that not all selected campaigns have children.
     * @param {Array<Object>} selectedCampaigns - The array of selected campaign objects.
     * @returns {boolean} Returns true if exactly one 'planit' campaign is selected and not all selected campaigns have children; otherwise, false.
     */
    checkIfOnePlanitCampaignIsSelectedOrNot = (selectedCampaigns) => {
        // Check if the key value pair exists in any object.
        const hasKeyValuePair = selectedCampaigns && selectedCampaigns.some(obj => obj.hasOwnProperty('source') && obj['source'] === 'planit');
        // If the key value pair exists in any object, check if it exists in more than one object.
        if (hasKeyValuePair) {
            const count = selectedCampaigns.reduce((acc, obj) => (acc + (obj.hasOwnProperty('source') && obj['source'] === 'planit')), 0);
            return count < 2 && !this.checkIfOnlyCampaignsWithChildrenAreSelected(selectedCampaigns);
        }
        return false;
    }

    /**
     * Checks if all selected campaigns have children (i.e., `nested` is true) and
     * exactly one of them has a `source` property equal to 'planit'.
     * @param {Array<Object>} selectedCampaigns - The array of selected campaign objects.
     * @returns {boolean} Returns true if all campaigns are nested and exactly one has source 'planit'; otherwise, false.
     */
    checkIfOnlyCampaignsWithChildrenAreSelected = (selectedCampaigns) => {
        return selectedCampaigns.every(obj => obj.nested === true) && selectedCampaigns.filter(obj => obj.hasOwnProperty('source') && obj['source'] === 'planit')?.length === 1;
    }

    /**
     * Adds the given node and its ancestor nodes to the selectedNodes set,
     * but only if all children of each ancestor node are selected.
     * Traverses up the tree from the given node, checking at each level if all
     * children of the current node are selected. If so, adds the node's data to
     * the selectedNodes set. Stops traversing when a node is found whose children
     * are not all selected or when there are no more ancestors.
     * @param {Object} node - The starting node to add and traverse upwards from.
     *   Expected to have `data`, `childrenAfterGroup`, `isSelected()`, and `parent` properties.
     * @param {Set} selectedNodes - A Set to which the node data will be added if the criteria are met.
     */
    addNodeAndAncestors = (node, selectedNodes) => {
        while (node) {
            if (node.data) {
                // Check if all children are selected
                const allChildrenSelected = node.childrenAfterGroup?.every(child => child.isSelected());
                if (!allChildrenSelected) {
                    break; // Stop adding ancestors if not all children are selected
                }
                selectedNodes.add(node.data);
            }
            node = node.parent; // Move to the parent node, continue until no parent exists
        }
    };

    /**
     * Handles the selection of a row in the grid and synchronizes the selection state
     * with its siblings and parent node.
     * - When a row at level 1 is selected or deselected, this function updates all its sibling rows
     *   to match the selection state of the current row.
     * - After updating siblings, it checks if all siblings are selected or any are selected,
     *   and updates the parent node's selection state accordingly.
     * - Prevents infinite loops by only updating the parent if its selection state differs from the calculated state.
     * @param {Object} event - The event object from the row selection.
     * @param {Object} event.node - The node representing the selected row.
     */
    onRowSelected = (event) => {
        const node = event.node;
        // Update siblings
        if (node.parent && node.parent.childrenAfterGroup && node.level === 1) {
            const siblings = node.parent.childrenAfterGroup;
            siblings.forEach((sibling) => {
                if (sibling.id !== node.id && sibling.isSelected() !== node.isSelected()) {
                    sibling.setSelected(node.isSelected(), false); // false = no recursion
                }
            });
            // Now update parent selection based on all children
            const allSelected = siblings.every((child) => child.isSelected());
            const anySelected = siblings.some((child) => child.isSelected());
            // Avoid infinite loop by checking current state
            if (node.parent.selectable && node.parent.isSelected() !== allSelected) {
                node.parent.setSelected(allSelected, false);
            }
        }
    };

    onSortChanged = (event) => {
        const sortColumn = event?.columns[0]?.colDef?.field;
        this.setState({
            searchEnable: false,
            searchColumn: '',
            searchParam: '',
            sortColumn,
            sortOrder: this.state.sortOrder === 'Desc' ? 'Asc' : 'Desc',
            sortingEnable: true,
            hasNext: '',
        }, () => { this.loadCampaigns(true) })
    };

    /**
     * Function to handle the restore Campaign functionality in Actions tab
     * This will call the campaign API to update the isArchive flag to false
     * @param {object} campaignObj - campaign object
    */
    unArchiveCampaign = (campaignObj) => {
        this.props.setSpinnerCount(1);
        let payload = { "isArchive": false };
        let campaignId = encodeURIComponent(campaignObj.id);
        MediaOpsService.updateCampaign(payload, campaignId, this.props?.versionOneApi).then((response) => {
            this.props.setSpinnerCount(0);
            AlertMessagesService.showSuccessToastMessage(`${campaignObj.campaign_name} has been restored successfully`);
            this.loadCampaigns();
        }).catch(() => {
            this.props.setSpinnerCount(0);
            AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.unArchiveCampaign(campaignObj), title: `Unable to restore the ${campaignObj.campaign_name}`, bodyMsgArr: [] }]);
        })
    }

    /**
    * Sets the active tab and retrieves the Campaigns list if the active tab is not already set.
    * @returns {void}
    */
    setActiveTab = () => {
        if (this.state.archiveTab) {
            this.setState({ archiveTab: false, campaignsNestedStructure: [] }, () => this.loadCampaigns());
        }
    }

    /**
     * Sets the archive tab and triggers the retrieval of Campaigns list if the active tab is currently set.
     * @returns {void}
     */
    setArchiveTab = () => {
        if (!this.state.archiveTab) {
            this.setState({ archiveTab: true, campaignsNestedStructure: [] }, () => this.loadCampaigns());
        }
    }

    /**
     * Handles the expansion of a campaign node in the grid.
     * @param {Object} event - The event object from the row expansion.
     */
    onExpandClick = (event) => {
        if (event?.expanded) {
            const node = event.node;
            if(!node.data.nested) {
                this.loadCampaigns(false, node.data);
            }
        }
    };

    applyFilter = (options, clearAll=false) => {
        if (clearAll) {
            this.loadCampaigns(true);
        }else{
            this.props.setSpinnerCount(1);
            MediaOpsService.getCampaignDataAggrid(options, null, this.state.archiveTab, this.props?.versionOneApi).then(response => {
                if (!response) return;
    
                const campaignsFromAPI = this.processCampaignData(response, true);
                const uniqueCampaigns = this.removeDuplicateCampaigns(campaignsFromAPI, true);
                const campaignsNestedStructure = MediaOpsService.transformDataAggrid(uniqueCampaigns);
                this.updateStateWithCampaigns(response, campaignsNestedStructure, campaignsFromAPI, null);
                this.props.setSpinnerCount(0);
            })
        }
    }

    render() {
        // Set up the context for AG-Grid components
        const gridContext = {
            componentParent: this // Remove onTaxonomyPartClick from context
        };

        const statusBar = {
            statusPanels: [
                {
                    statusPanel: "agAggregationComponent",
                    statusPanelParams: { aggFuncs: ["avg", "sum"] },
                },
                {
                    statusPanel: CustomStatusBar,
                    align: "left",
                    statusPanelParams: {
                        component: "campaigns",
                        totalCount: this.state.totalCount, // Pass total count dynamically
                    },
                },
            ],
        };

        return (
            <>
                {/* Top warning bar: shows count of unmapped Prisma files and CTA to map them */}
                {this.state?.unMapCampaign?.length > 0  && (
                        (() => {
                            const unmappedCount = (this.state.unMapCampaign && this.state.unMapCampaign.length) ? this.state.unMapCampaign.length : 0;
                            let message = `We couldn't map ${unmappedCount} Prisma files. Please map them to the right media plan for accurate reporting.`;
                            if(this.props.isMappingTable){
                                message = `Drag and drop the items from the right panel to the appropriate campaign.`;
                            }
                            return (
                                <div className={`campaign-text-bar ${this.props.isMappingTable ? 'info-bar':'warning-bar'}`} >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <OmniIconComponent icon-id={`omni:informative:${this.props.isMappingTable ? 'error':'alert'}`} className="is-size-4" />
                                        <div style={{ lineHeight: '1.1' }}>
                                            <div style={{ fontWeight: 600 }}>{message}</div>
                                        </div>
                                    </div>
                                    
                                    {!this.props.isMappingTable && <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <button className="secondary" onClick={() => this.props.setMappingTable(true)} style={{ minWidth: '120px' }}>Map Campaigns</button>
                                    </div>}
                                </div>
                            );
                        })()
                )}
                <div className='campaigns-table-container ag-theme-alpine' style={{ height: "calc(100vh - 160px)", width: "98%", padding: "20px 20px 55px 20px" }}>
                    {/* keeping the code for future reference */}
                    {/* <OmniToolbarComponent slot='header'>
                        <div slot='start'>
                            <button className={`medium activeButton ${!this.state.archiveTab ? '' : 'tab'}`} onClick={this.setActiveTab}>Active Campaigns</button>
                            <button className={`medium ${this.state.archiveTab ? '' : 'tab'}`} onClick={this.setArchiveTab}>Archived Campaigns</button>
                        </div>
                    </OmniToolbarComponent> */}

                    {this.gridApi && <FilterCampaign onApplyFilter={this.applyFilter.bind(this)} gridApi={this.gridApi} isTabRequired={true} id={'campaign-table'}/>}
            
                    <AgGridReact
                        key="campaigns-ag-grid-v3"
                        ref={this.gridRef}
                        columnDefs={this.state.columnDefs}
                        rowData={this.state.campaignsNestedStructure}
                        autoGroupColumnDef={this.state.autoGroupColumnDef}
                        treeData={true}
                        treeDataChildrenField={"nestedData"}
                        getRowId={(params) => params?.data?.id?.toString()}
                        onRowGroupOpened={this.onExpandClick}
                        onCellClicked={this.onCellClicked}
                        defaultColDef={defaultColDef}
                        sideBar={true}
                        context={gridContext}
                        domLayout="normal"
                        onGridReady={this.onGridReady}
                        onSortChanged={this.onSortChanged}
                        onRowSelected={this.onRowSelected}
                        statusBar={statusBar}
                    />
                </div>
            </>
        );
    }
}

export default CampaignTableAgGridV2;